import React from "react";
import { Icon } from "../core/Icon.jsx";

/* Select / dropdown. Closed field shows value or placeholder + caret.
   Open menu = white card, hover row → light mint, selected row → vivid green.
   options: [{value,label}] or string[].
   searchable: free-text filter in the open menu (legacy ReactSelect hideSearch=false). */
let _sid = 0;

export function Select({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select",
  searchPlaceholder = "Search",
  error,
  helper,
  disabled = false,
  required = false,
  size = "lg",
  searchable = false,
  style,
  ...rest
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [active, setActive] = React.useState(0);
  const ref = React.useRef(null);
  const searchRef = React.useRef(null);
  const id = React.useMemo(() => `tk-sel-${++_sid}`, []);
  const listId = `${id}-listbox`;
  const norm = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const current = norm.find((o) => o.value === value);
  const height = size === "sm" ? 36 : size === "md" ? 44 : 48;
  const showSearch = searchable && norm.length > 0;

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return norm;
    return norm.filter((o) => o.label.toLowerCase().includes(q));
  }, [norm, query]);

  React.useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) closeMenu();
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  React.useEffect(() => {
    if (open && showSearch && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open, showSearch]);

  React.useEffect(() => {
    setActive(0);
  }, [query, open]);

  const closeMenu = () => {
    setOpen(false);
    setQuery("");
    setActive(0);
  };

  const openMenu = () => {
    if (disabled) return;
    setOpen(true);
  };

  const selectValue = (next) => {
    if (onChange) onChange(next);
    closeMenu();
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      if (filtered[active]) {
        e.preventDefault();
        selectValue(filtered[active].value);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeMenu();
    }
  };

  let border = "inset 0 0 0 1px var(--border)";
  if (error) border = "inset 0 0 0 1.5px var(--red)";
  else if (open) border = "inset 0 0 0 1.5px var(--green-bright)";

  return (
    <div ref={ref} style={{ display: "flex", flexDirection: "column", gap: 6, position: "relative", ...style }}>
      {label ? (
        <label htmlFor={id} style={{ fontFamily: "var(--font-sans)", fontWeight: 600, fontSize: 14, color: "var(--text-body)" }}>
          {label}{required ? <span style={{ color: "var(--red)" }}> *</span> : null}
        </label>
      ) : null}
      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => (open ? closeMenu() : openMenu())}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          height,
          padding: "0 12px 0 14px",
          border: "none",
          outline: "none",
          borderRadius: "var(--radius-xs)",
          background: disabled ? "var(--grey-bg)" : "var(--white)",
          boxShadow: disabled ? "inset 0 0 0 1px var(--border)" : border,
          fontFamily: "var(--font-sans)",
          fontWeight: 500,
          fontSize: 14,
          color: current ? "var(--text-body)" : "var(--text-muted)",
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "box-shadow var(--dur) var(--ease)",
          textAlign: "left",
        }}
        {...rest}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {current ? current.label : placeholder}
        </span>
        <Icon name="chevronDown" size={18} color="var(--text-muted)" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform var(--dur) var(--ease)" }} />
      </button>

      {open ? (
        <div
          role="listbox"
          id={listId}
          aria-label={label || placeholder}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            zIndex: 30,
            background: "var(--white)",
            borderRadius: "var(--radius-sm)",
            boxShadow: "var(--shadow-inset-border), var(--shadow-3)",
            padding: 6,
            maxHeight: 260,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {showSearch ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                height: 36,
                flexShrink: 0,
                padding: "0 10px",
                borderRadius: "var(--radius-xs)",
                boxShadow: "inset 0 0 0 1px var(--border)",
                background: "var(--white)",
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Icon name="search" size={16} color="var(--text-muted)" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  background: "transparent",
                  fontFamily: "var(--font-sans)",
                  fontWeight: 500,
                  fontSize: 14,
                  color: "var(--text-body)",
                }}
              />
            </div>
          ) : null}

          {filtered.length > 0 ? (
            filtered.map((o, index) => {
              const selected = o.value === value;
              const highlighted = index === active;
              const optionDisabled = o.disabled === true;
              return (
                <Row
                  key={o.value}
                  id={`${listId}-opt-${index}`}
                  selected={selected}
                  highlighted={highlighted}
                  disabled={optionDisabled}
                  onMouseEnter={() => !optionDisabled && setActive(index)}
                  onClick={() => !optionDisabled && selectValue(o.value)}
                >
                  <span>{o.label}</span>
                  {selected ? <Icon name="check" size={16} color="var(--green-success)" /> : null}
                </Row>
              );
            })
          ) : (
            <div
              style={{
                padding: "10px 12px",
                fontFamily: "var(--font-sans)",
                fontWeight: 500,
                fontSize: 14,
                color: "var(--text-muted)",
              }}
            >
              No result found
            </div>
          )}
        </div>
      ) : null}

      {error || helper ? (
        <div style={{ fontFamily: "var(--font-sans)", fontWeight: 500, fontSize: 12, color: error ? "var(--red)" : "var(--text-muted)" }}>
          {error || helper}
        </div>
      ) : null}
    </div>
  );
}

function Row({ children, selected, highlighted, disabled, onClick, onMouseEnter, id }) {
  const [hover, setHover] = React.useState(false);
  const active = !disabled && (selected || highlighted || hover);
  return (
    <div
      id={id}
      role="option"
      aria-selected={selected}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => {
        if (disabled) return;
        setHover(true);
        if (onMouseEnter) onMouseEnter();
      }}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        height: 38,
        // Rows are flex children of the scrolling column menu; without this a long list that
        // overflows maxHeight gets shrunk by flexbox to content height (~16px). Keep them at 38px.
        flexShrink: 0,
        padding: "0 10px",
        borderRadius: "var(--radius-xs)",
        background: selected ? "var(--mint-light)" : active ? "var(--grey-bg)" : "transparent",
        color: disabled ? "var(--text-muted)" : "var(--text-body)",
        fontFamily: "var(--font-sans)",
        fontWeight: selected ? 600 : 500,
        fontSize: 14,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </div>
  );
}

export default Select;
